// eslint-disable-next-line no-redeclare
import _ from 'lodash';
import { setParameter, getParameter, getUrlPathAndSearch } from '@hotjar/insights-common/lib/url';
import { log } from '@hotjar/insights-common/lib/log';
import { getWidgetConfiguration } from '@hotjar/integrations/domains/slack/api';
import { useNavigationStore } from '@hotjar/navigation';
import { mapFiltersToDto, mapFiltersToDaysAgo } from '@hotjar/feedback/domain/IncomingFeedback/filters/FiltersMappings';
import {
    DEFAULT_DATE_FILTER,
    DEFAULT_INCOMING_FEEDBACK_RESPONSES_FILTERS,
    DEFAULT_RATING_FILTER,
    IncomingFeedbackFilters
} from '@hotjar/feedback/domain/IncomingFeedback/filters/ResponsesFilters';
import { noop } from '@hotjar/webapp/lib/function';
import { DATE_RANGE_TYPE, DATE_UNITS } from '@hotjar/global-filters';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { getItemsPerPage } from '@hotjar/webapp/lib/itemsPerPage';
import {
    mutationDeleteFeedbackResponse,
    queryDefaultFeedbackWidget,
    mergeFilterParams,
    encodeFilterParams,
    areAllRatingsIncluded
} from '@hotjar/feedback';
import { getRecordingUrl, getRecordingUrls } from '@hotjar/webapp/domains/recordings/api/recordings';
import { SIDE_BAR_WIDTH } from '@hotjar/feedback/components/common/SideBar/SideBar';
import { hotjar } from '@hotjar/tracking';
import { i18next } from '@hotjar/translations';
import { experimentService, VARIANTS } from '@hotjar/experimentation';
import { mapSiteDtoToModel } from '@hotjar/site';

angular.module('hjFeedbackModule').controller('FeedbackResponsesCtrl', [
    '$scope',
    '$state',
    '$stateParams',
    '$timeout',
    '$location',
    'AskFeature',
    'GQSR',
    'DownloadBlob',
    'ModalPrompt',
    'Notify',
    'Restangular',
    'restDefaultHandlers',
    'Track',
    'TrackingCode',
    'UserData',
    'Organization',
    'FeedbackQuery',
    'Loader',
    'Feature',
    function (
        $scope,
        $state,
        $stateParams,
        $timeout,
        $location,
        AskFeature,
        GQSR,
        DownloadBlob,
        ModalPrompt,
        Notify,
        Restangular,
        restDefaultHandlers,
        Track,
        TrackingCode,
        UserData,
        Organization,
        FeedbackQuery,
        Loader
    ) {
        if ($stateParams.newToolCreated) {
            Track.createdIncomingFeedback();
        }

        /*************************
         * PRIVATE VARIABLES
         *************************/
        var dataFetchTimeout,
            feedbackApi,
            FIFTEEN_MINUTES_IN_MS = 15 * 60 * 1000,
            reloadResponsesTimeout;

        /*************************
         * SCOPE VARIABLES
         *************************/

        $scope.feedbackWidgets = [];
        $scope.feedbackResponses = [];
        $scope.feedbackResponsesCount = 0;

        $scope.model = {
            currentSite: UserData.currentSite,
            siteId: Number($stateParams.siteId),
            feedbackWidgetId: Number($stateParams.feedbackWidgetId),
            currentPage: 1,
            currentSiteId: UserData.currentSite?.id,
            // Combine all date ranges from Constants with another entry for all, which adds the last 10+ years of data.
            itemsPerPage: getItemsPerPage(),
            loaded: false,
            loadingGQSRData: false,
            feedbackWidget: null,
            feedbackWidgetsCount: 0,
            viewMode: 'responses',
            alertLocation: 'feedback-responses',
            responsesViewMode: LocalStorage.get('incomingResponsesViewMode') || 'card',
            responsesViewModeChoices: [
                { value: 'card', label: 'Card view', icon: 'icon-grid' },
                { value: 'list', label: 'List view', icon: 'icon-details' }
            ],
            responsesDownloadLimit: 30000,
            trackingCode: TrackingCode.getState(UserData.currentSite),
            slackForwardingEnabled: null,
            organizationUsers: null,
            coveragePercentage: 0,
            inEmptyStateExperiment: false,
            contentContainerWidth: `calc(100% - ${SIDE_BAR_WIDTH})`
        };

        $scope.filters = mergeFilterParams(
            { ...DEFAULT_INCOMING_FEEDBACK_RESPONSES_FILTERS },
            new URLSearchParams(window.location.search)
        );

        Track.incomingFeedbackResponsesViewModeViewed($scope.model.responsesViewMode);

        async function getFeedbackWidgetId() {
            if ($scope.model.feedbackWidgetId) {
                return $scope.model.feedbackWidgetId;
            }

            const defaultFeedbackWidget = await queryDefaultFeedbackWidget($scope.model.currentSite);

            if (defaultFeedbackWidget) return defaultFeedbackWidget.id;

            throw new Error('No feedback widgets found');
        }

        /*************************
         * INITIALIZATION
         *************************/

        Loader.suppress(init());

        /*************************
         * PRIVATE FUNCTIONS
         *************************/

        function getResponseId() {
            const fridParam = parseInt(getParameter($location.absUrl(), 'frid'), 10);

            return isNaN(fridParam) ? null : fridParam;
        }

        async function loadData() {
            $scope.feedbackWidgets = await FeedbackQuery.queryFeedbackWidgets($scope.model.currentSite);

            $scope.isEmpty = $scope.feedbackWidgets.length === 0;

            $scope.isEmptyState = false;

            if ($scope.isEmpty) {
                if (UserData?.currentSite) {
                    const mappedSite = mapSiteDtoToModel(UserData.currentSite);
                    const experimentResult = await experimentService.startAccountExperiment(
                        'atv_empty_states_2023_03',
                        mappedSite.account,
                        mappedSite
                    );

                    $scope.model.inEmptyStateExperiment = experimentResult.variant === VARIANTS.variant1;
                    $scope.model.contentContainerWidth = $scope.model.inEmptyStateExperiment
                        ? '100%'
                        : $scope.model.contentContainerWidth;
                }

                $scope.model.loaded = true;
                $scope.isEmptyState = true;
                return;
            }

            $scope.model.feedbackWidgetId = await getFeedbackWidgetId();

            getTotalFeedbackWidgetsCount();
            getSlackIntegrationStatus();
            getCoveragePercentage();

            feedbackApi = '/sites/' + $scope.model.siteId + '/feedback/' + $scope.model.feedbackWidgetId;

            var responseId = getResponseId();

            const searchParams = new URLSearchParams(window.location.search);

            $state.go(
                $state.current.name,
                {
                    siteId: $scope.model.siteId,
                    feedbackWidgetId: $scope.model.feedbackWidgetId,
                    frid: responseId
                },
                { location: 'replace' }
            );

            for (const [key, value] of searchParams) {
                $location.search(key, value);
            }

            $scope.model.feedbackWidget = $scope.feedbackWidgets.find(
                (feedback) => feedback.id === $scope.model.feedbackWidgetId
            );

            if (!$scope.model.feedbackWidget) {
                $scope.model.loaded = true;
                return;
            }

            await getResponses(true);

            // Show the details for a single response if a frid (feedback response id) is given in the URL.
            // Warning: In case of query parameter name change, please do not forget to update backend
            // accordingly (feedback_api.py)
            if (responseId) {
                const response = _.find($scope.feedbackResponses, { id: responseId });

                if (!response) {
                    getSingleResponse(responseId, function (res) {
                        setRecordingUrl(res.data[0]);
                    });
                } else {
                    $scope.showResponseDetails(response);
                }
            }
            $scope.model.loaded = true;
        }

        async function init() {
            Track.view('Incoming Feedback: List');
            Track.view('Incoming Feedback: Responses view');

            await loadData();
        }

        /**
         * Set recording URL for a response.
         */
        function setRecordingUrl(response) {
            const autotagNameValues = {
                e: response.content.emotion,
                id: $scope.model.feedbackWidgetId,
                r_id: response.id
            };
            // eslint-disable-next-line max-len
            const tag = `feedback.sentiment.e:${autotagNameValues.e}.id:${autotagNameValues.id}.r_id:${autotagNameValues.r_id}`;

            let recordingUrl;

            return getRecordingUrl($scope.model.siteId, tag)
                .then(function (response) {
                    recordingUrl = response;
                })
                .catch(function (response) {
                    restDefaultHandlers.onError(response);
                })
                .finally(() => {
                    $scope.showResponseDetails({ ...response, recording_url: recordingUrl });
                });
        }

        /**
         * Get the total count of feedback widgets created on the current site, used to determine whether the user has
         * reached their feedback widget limit (e.g. max 3 on BASIC).
         */
        function getTotalFeedbackWidgetsCount() {
            GQSR.get(
                {
                    apiEndpoint: '/sites/' + $scope.model.siteId + '/feedback/count',
                    version: '1',
                    apiPrefix: AskFeature.isAllowedForAsk('ask.separate_service') ? 'ask' : undefined
                },
                function (res) {
                    $scope.model.feedbackWidgetsCount = res.count;
                }
            );
        }

        function getSingleResponse(responseId, callback) {
            GQSR.get(
                {
                    apiEndpoint: feedbackApi + '/responses',
                    version: '3',
                    search_spec: JSON.stringify({
                        clauses: [{ type: 'document_id', value: responseId }]
                    }),
                    apiPrefix: AskFeature.isAllowedForAsk('ask.separate_service') ? 'ask' : undefined
                },
                function (res) {
                    if (_.isFunction(callback)) {
                        callback(res);
                    }
                }
            );
        }

        /**
         * Set recording URLs for responses list.
         */
        function setRecordingsUrls(responses) {
            const tags = responses.map((response) => {
                const autotagNameValues = {
                    e: response.content.emotion,
                    id: $scope.model.feedbackWidgetId,
                    r_id: response.id
                };
                /* eslint-disable-next-line max-len */
                return `feedback.sentiment.e:${autotagNameValues.e}.id:${autotagNameValues.id}.r_id:${autotagNameValues.r_id}`;
            });

            let recordingUrls = [];

            const createdDaysAgo = mapFiltersToDaysAgo(
                mapFiltersToDto($scope.filters),
                new Date($scope.model.feedbackWidget.created_epoch_time * 1000)
            );

            return getRecordingUrls($scope.model.siteId, tags, createdDaysAgo)
                .then(function (response) {
                    recordingUrls = response;
                })
                .catch(function (response) {
                    restDefaultHandlers.onError(response);
                })
                .finally(() => {
                    const mergedRecordings = responses.map((response) => {
                        const recordingsForResponse = recordingUrls.find(
                            (recordingUrl) => recordingUrl.response_id === response.id
                        );
                        return recordingsForResponse
                            ? {
                                  ...response,
                                  recording_url: recordingsForResponse.url
                              }
                            : response;
                    });

                    $scope.feedbackResponses = mergedRecordings;
                });
        }

        function getResponses(responseCountChanged, callback) {
            // A short delay is set to display a spinner to avoid a 'spinner flicker' when a request returns quickly.
            dataFetchTimeout = $timeout(function () {
                $scope.model.loadingGQSRData = true;
            }, 50);

            const getPromise = GQSR.get(
                {
                    apiEndpoint: feedbackApi + '/responses',
                    version: '3',
                    search_spec: JSON.stringify({
                        clauses: mapFiltersToDto($scope.filters),
                        page_size: $scope.model.itemsPerPage,
                        page_number: $scope.model.currentPage,
                        sort_by: $scope.getSort()
                    }),
                    apiPrefix: AskFeature.isAllowedForAsk('ask.separate_service') ? 'ask' : undefined
                },
                function (res) {
                    setRecordingsUrls(res.data);

                    if (responseCountChanged) {
                        $scope.feedbackResponsesCount = res.count;
                        setDownloadAvailability();
                        getTotalAndAverageResponses();
                    }
                    if (_.isFunction(callback)) {
                        callback();
                    }

                    $timeout.cancel(dataFetchTimeout);
                    $scope.model.loadingGQSRData = false;
                }
            );

            Organization.users(UserData.currentSite.organization)
                .then((users) => {
                    $scope.model.organizationUsers = users.reduce(
                        (acc, user) => ({
                            ...acc,
                            [user.id]: user
                        }),
                        {}
                    );
                })
                .catch(angular.noop);

            scheduleReload();

            return getPromise;
        }

        /**
         * Get data for Slack forwarding feature for this particular feedback
         */
        function getSlackIntegrationStatus() {
            return getWidgetConfiguration($scope.model.siteId, 'incoming_feedback', $scope.model.feedbackWidgetId)
                .then(
                    ({ data: { enabled = false } = {} }) => enabled,
                    () => false
                )
                .then((enabled) =>
                    $timeout(() => {
                        $scope.model.slackForwardingEnabled = enabled;
                    })
                );
        }

        /**
         * Get coveragePercentage value for Play recording buttons segment tracking
         */
        function getCoveragePercentage() {
            UserData.getCoveragePercentage().then((coveragePercentage) => {
                $scope.coveragePercentage = coveragePercentage;
            });
        }

        function scheduleReload() {
            clearScheduledResponseReload();

            reloadResponsesTimeout = $timeout(function () {
                getResponses(true);
            }, FIFTEEN_MINUTES_IN_MS);
        }

        function clearScheduledResponseReload() {
            if (reloadResponsesTimeout) {
                $timeout.cancel(reloadResponsesTimeout);
            }
        }

        $scope.downloadDisabled = undefined;
        $scope.downloadDisabledTooltip = i18next.t('feedback:incoming-feedback.export-results.disabled-tooltip', {
            count: Intl.NumberFormat().format($scope.model.responsesDownloadLimit, i18next.language)
        });

        function setDownloadAvailability() {
            $scope.downloadDisabled = $scope.feedbackResponsesCount > $scope.model.responsesDownloadLimit;
        }

        function getTotalAndAverageResponses() {
            // Load the sentiment info for all responses.
            GQSR.get(
                {
                    apiEndpoint: feedbackApi + '/results',
                    version: '3',
                    search_spec: JSON.stringify({
                        clauses: mapFiltersToDto($scope.filters)
                    }),
                    apiPrefix: AskFeature.isAllowedForAsk('ask.separate_service') ? 'ask' : undefined
                },
                function (res) {
                    $scope.model.sentimentCount = res.sentiment;
                    $scope.averageDailySentiment = $scope.model.averageDailySentiment = res.avg_daily_sentiment;
                    $scope.model.averageSentiment = getAverageSentiment();
                    $scope.model.dailyResponseCount = res.daily_response_count;
                    $scope.model.loaded = true;

                    if ($scope.model.viewMode === 'results') {
                        drawSentimentGraph();
                    }
                }
            );
        }

        function getAverageSentiment() {
            var sum = 0,
                countTotal;

            // We add + 1 to all values so the average is a number between 1 and 5, not 0 and 4.
            _.each(
                $scope.model.sentimentCount,
                function (value, key) {
                    countTotal = value * (parseInt(key) + 1);
                    sum = sum + countTotal;
                },
                0
            );

            return sum / $scope.feedbackResponsesCount;
        }

        function drawSentimentGraph() {
            $scope.showSentimentGraph = true;
        }

        /**
         * Reload masonry within the grid view mode.
         */
        function reloadMasonry() {
            if ($scope.model.viewMode === 'grid') {
                $timeout(function () {
                    $scope.$broadcast('masonry.reload');
                }, 10);
            }
        }

        /*************************
         * SCOPE FUNCTIONS
         *************************/

        /**
         * Create a new Feedback Widget.
         */
        $scope.createFeedbackWidget = function () {
            if ($scope.featureLimits().reached) {
                $scope.showUpsellModal('feedback.limit.reached');
                return;
            }
            $state.go('feedback.create');
        };

        /**
         * Get any limits we have.
         */
        $scope.featureLimits = function () {
            return AskFeature.getLimitsForAsk('feedback', $scope.feedbackWidgets.length);
        };

        function setFilterParams(params) {
            window.history.replaceState({}, '', `${location.pathname}?${params}`);
        }

        function setFilters(filters) {
            $scope.filters = filters;
            setFilterParams(encodeFilterParams(filters));
            getResponses(true);
        }

        function resetFilters() {
            setFilters({ ...DEFAULT_INCOMING_FEEDBACK_RESPONSES_FILTERS });
        }

        $scope.onFilterChange = (newFilterValue) => {
            setFilters({ ...$scope.filters, [newFilterValue.type]: newFilterValue });
        };

        $scope.onRemoveFilter = (filterType) => {
            setFilters({ ...$scope.filters, [filterType]: undefined });
        };

        function getResponse(responseId) {
            const index = $scope.feedbackResponses.findIndex((response) => response.id === responseId);
            if (index === -1) return undefined;

            return $scope.feedbackResponses[index];
        }

        function getModalResponse(responseId) {
            return $scope.modalResponse?.id === responseId ? $scope.modalResponse : undefined;
        }

        $scope.setResponseFeedback = (votingAction, responseId) => {
            const response = getResponse(responseId) ?? getModalResponse(responseId);

            if (response === undefined) return;

            response.voting_action = votingAction;

            if ($scope.modalResponse?.id === response.id) {
                $scope.modalResponse = { ...response };
            }
            // this is to detect changes for the table
            $scope.feedbackResponses = [...$scope.feedbackResponses];
        };

        $scope.isFeedbackColumnShown = () => {
            return ($scope.feedbackResponses ?? []).length > 3;
        };

        /**
         * Delete a feedback response (after showing a confirmation).
         *
         * @param response Response to delete.
         * @param callback Callback to execute after deletion.
         */
        $scope.deleteResponse = function (response, callback) {
            ModalPrompt.show(
                i18next.t('feedback:EntityActions.delete-response.modal-title'),
                i18next.t('feedback:EntityActions.delete-response.confirmation'),
                i18next.t('feedback:EntityActions.delete-response.delete'),
                i18next.t('feedback:EntityActions.delete-response.cancel'),
                function () {
                    mutationDeleteFeedbackResponse($scope.model.currentSite, $scope.model.feedbackWidgetId, response.id)
                        .then(() => {
                            $scope.feedbackResponses = _.reject($scope.feedbackResponses, function (r) {
                                return r.id === response.id;
                            });
                            Notify.success(i18next.t('feedback:feedback-responses.notification.delete-success'));

                            // Reduce total count and update the totals and averages.
                            // This will also redraw the sentiment graph.
                            $scope.feedbackResponsesCount--;
                            setDownloadAvailability();
                            getTotalAndAverageResponses();

                            if (callback) {
                                callback();
                            }
                        })
                        .catch(() => {
                            Notify.error(i18next.t('feedback:feedback-responses.notification.delete-error'));
                        });
                }
            );
        };

        /**
         * Edit a Feedback Widget.
         */
        $scope.editFeedbackWidget = function () {
            $state.go('feedback.edit', { feedbackWidgetId: $scope.model.feedbackWidget.id });
        };

        $scope.editWidgetForwarding = function () {
            $state.go('feedback.edit', {
                feedbackWidgetId: $scope.model.feedbackWidget.id,
                activeFlowSection: 'forwarding'
            });
        };

        $scope.onUpsellGlobalFilters = (referrer) => {
            $scope.showUpsellModal(referrer);
        };

        /**
         * Create a new Feedback Widget.
         */
        $scope.createFeedbackWidget = function () {
            if (AskFeature.getLimitsForAsk('feedback', $scope.model.feedbackWidgetsCount).reached) {
                $scope.showUpsellModal('feedback.limit.reached');
            } else {
                $state.go('feedback.create');
            }
        };

        /**
         * React to page changes.
         */
        $scope.$watch('model.currentPage', function (newValue, oldValue) {
            var scrollPosition, scrollToPosition;

            if (newValue !== oldValue) {
                getResponses(false);

                // Scroll to the top if needed.
                scrollPosition = window.document.body.scrollTop || window.document.documentElement.scrollTop;
                scrollToPosition = $('#top-paging').offset().top;

                if (scrollPosition > scrollToPosition) {
                    window.scrollTo(0, scrollToPosition - 135);
                }
            }
        });

        /**
         * Set the view Mode to a specific value.
         * @param viewMode Param to change the view mode.
         */
        $scope.setViewMode = function (viewMode) {
            $scope.model.viewMode = viewMode;
        };

        /**
         * Reset all filters back to their default values.
         */
        $scope.resetFilters = function () {
            resetFilters();
        };

        $scope.isDefaultDateRange = function () {
            const dateFilter = $scope.filters[IncomingFeedbackFilters.DATE];
            const defaultDateFilter = DEFAULT_DATE_FILTER;
            return (
                dateFilter &&
                defaultDateFilter &&
                dateFilter.value.unit === defaultDateFilter.value.unit &&
                dateFilter.value.amount === defaultDateFilter.value.amount
            );
        };

        $scope.setDateRange = function (rangeInDays) {
            const dateFilter = {
                type: IncomingFeedbackFilters.DATE,
                value: {
                    type: DATE_RANGE_TYPE.RELATIVE
                }
            };
            switch (rangeInDays) {
                case '7':
                    dateFilter.value.unit = DATE_UNITS.DAYS;
                    dateFilter.value.amount = 7;
                    break;
                case '15':
                    dateFilter.value.unit = DATE_UNITS.DAYS;
                    dateFilter.value.amount = 15;
                    break;
                case '30':
                    dateFilter.value.unit = DATE_UNITS.DAYS;
                    dateFilter.value.amount = 30;
                    break;
                case '90':
                    dateFilter.value.unit = DATE_UNITS.MONTHS;
                    dateFilter.value.amount = 3;
                    break;
                case '180':
                    dateFilter.value.unit = DATE_UNITS.MONTHS;
                    dateFilter.value.amount = 6;
                    break;
                case '365':
                    dateFilter.value.unit = DATE_UNITS.MONTHS;
                    dateFilter.value.amount = 12;
                    break;
            }
            $scope.onFilterChange(dateFilter);
        };

        /**
         * Functions to execute when the view mode is changed
         */
        let cachedRatingFilter;

        $scope.onViewModeChange = function (newViewMode) {
            if (newViewMode !== undefined) {
                $scope.model.viewMode = newViewMode;
                $scope.model.alertLocation = `feedback-${newViewMode}`;
            }

            $timeout(function () {
                if ($scope.model.viewMode === 'results') {
                    Track.view('Incoming Feedback: Results view');

                    // We cannot show an average for a specific sentiment.
                    cachedRatingFilter = null;
                    const ratingFilter = $scope.filters[IncomingFeedbackFilters.RATING];

                    if (ratingFilter && !areAllRatingsIncluded(ratingFilter.value)) {
                        cachedRatingFilter = ratingFilter;
                        $scope.onFilterChange(DEFAULT_RATING_FILTER);
                        getResponses(true);
                    } else {
                        drawSentimentGraph();
                    }
                } else {
                    // restore rating filter that was removed for the results page to get all data
                    if (cachedRatingFilter) {
                        $scope.onFilterChange(cachedRatingFilter);
                    }
                }
            }, 0);
        };

        $scope.onResponsesViewModeChange = function (newMode) {
            $timeout(() => {
                $scope.model.responsesViewMode = newMode;
                LocalStorage.set('incomingResponsesViewMode', newMode);
                Track.incomingFeedbackResponsesViewModeChanged(newMode);
            }, 0);
        };

        $scope.onAddResponseComment = (response) => $scope.showResponseDetails(response, true);
        $scope.onShowResponseComments = (response) => $scope.showResponseDetails(response, true);
        $scope.onResponseCardClick = (response) => $scope.showResponseDetails(response);

        /**
         * Callback of react-response-comment component.
         * Yeah, this is dirty. It uses React model to update DTO used on
         * Angular side. There is no guarantee that after refreshing the page
         * the data returned by server will be the same. It's fragile.
         *
         * @param {number} responseId
         * @param {number} authorId
         */
        function addComment(responseId, authorId) {
            const response = getResponse(responseId) ?? getModalResponse(responseId);

            if (response === undefined) {
                return;
            }

            if (!response.commented_by.includes(authorId)) {
                response.commented_by.push(authorId);
            }

            response.comment_count += 1;

            // ensure modal response is aligned
            if ($scope.modalResponse?.id === responseId) {
                $scope.modalResponse = response;
            }

            $scope.feedbackResponses = [...$scope.feedbackResponses];
        }

        function removeComment(responseId, authorId, wasLastAuthorComment) {
            const response = getResponse(responseId) ?? getModalResponse(responseId);

            if (response === undefined) {
                return;
            }

            if (wasLastAuthorComment) {
                response.commented_by = response.commented_by.filter((id) => id !== authorId);
            }
            response.comment_count -= 1;

            // ensure modal response is aligned
            if ($scope.modalResponse?.id === responseId) {
                $scope.modalResponse = response;
            }

            $scope.feedbackResponses = [...$scope.feedbackResponses];
        }

        $scope.isCommentBoxFocused = false;

        /**
         * Show the modal to display the response details.
         */
        $scope.showResponseDetails = function (response, focusComments = false) {
            if (typeof response === 'undefined') {
                log.warn('Could not find item to display.');
                return;
            }

            $scope.isCommentBoxFocused = focusComments;
            Track.loadedModal('Incoming Feedback response details', Track.getGroupDataForSite(UserData.currentSite));

            /**
             * Bind Next + Prev actions with arrow keys.
             */
            Mousetrap.bind('left', function () {
                $timeout($scope.previous, 0);
                return false;
            });

            Mousetrap.bind('right', function () {
                $timeout($scope.next, 0);
                return false;
            });

            selectResponse(response);
        };

        function downloadResponses(format, callback, async = 'false') {
            GQSR.get(
                {
                    apiEndpoint: feedbackApi + '/responses/export',
                    version: '3',
                    search_spec: JSON.stringify({
                        clauses: mapFiltersToDto($scope.filters),
                        sort_by: $scope.getSort()
                    }),
                    format: format,
                    async_export: async,
                    apiPrefix: AskFeature.isAllowedForAsk('ask.separate_service') ? 'ask' : undefined
                },
                callback
            );
        }

        /**
         * Download the incoming feedback results as a file.
         * @param format - The format in which to download responses in: 'csv' || 'xlsx'.
         */
        $scope.downloadResponses = function (format) {
            downloadResponses(format, (response) => {
                if (response.download_url) {
                    hotjar.event('downloaded_responses');
                    DownloadBlob.open(
                        response.download_url,
                        'feedback-' + $stateParams.feedbackWidgetId + '.' + format + '.zip',
                        false
                    );
                } else if (response.above_threshold) {
                    ModalPrompt.show(
                        i18next.t('feedback:feedback-responses::export::exporting'),
                        i18next.t('feedback:feedback-responses::export::response-threshold-message', {
                            count: Intl.NumberFormat(i18next.language).format(response.above_threshold)
                        }),
                        i18next.t('feedback:feedback-responses::export::continue'),
                        i18next.t('feedback:feedback-responses::export::cancel'),
                        () => {
                            hotjar.event('downloaded_responses');
                            downloadResponses(format, noop, 'true');
                        },
                        null,
                        'primary'
                    );
                }
            });
            Track.downloadItem('Feedback Responses', format);
        };

        useNavigationStore.subscribe(() => {
            $scope.$applyAsync(() => {
                reloadMasonry();
            });
        });

        /**
         * Listen to siteLastDataRefreshed and update the model as needed.
         */
        $scope.$on('siteLastDataRefreshed', function (event, site) {
            $scope.model.trackingCode = TrackingCode.getState(site);
        });

        $scope.$on('$destroy', function () {
            clearScheduledResponseReload();
        });

        $scope.onPageChange = (page) => {
            $scope.model.currentPage = page;
        };

        $scope.onPageSizeChange = (pageSize) => {
            $scope.model.itemsPerPage = pageSize;
            getResponses(false);
        };

        $scope.onRatingClicked = (rating) => {
            $scope.onFilterChange({ value: [rating], type: IncomingFeedbackFilters.RATING });
            $scope.setViewMode('responses');
        };

        $scope.onResponsesClicked = () => {
            $scope.setViewMode('responses');
        };

        $scope.onSortChanged = (by, direction) => {
            const prefix = direction === 'desc' ? '-' : '';
            const sortBy = prefix + by;
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('sort_by', sortBy);
            window.history.replaceState(
                null,
                '',
                `${window.location.pathname}?${searchParams.toString()}`.replaceAll('%3A', ':')
            );
            LocalStorage.set('FBResponsesSort', sortBy);
            getResponses(false);
        };

        Object.defineProperty($scope, 'currentSiteId', {
            get() {
                return UserData.currentSite?.id;
            }
        });

        Object.defineProperty($scope, 'currentOrganizationId', {
            get() {
                return UserData.currentSite?.organization_id;
            }
        });

        let cachedUserData;
        Object.defineProperty($scope, 'currentUserData', {
            get() {
                if (!cachedUserData) {
                    const userData = UserData.getAll();

                    cachedUserData = {
                        id: userData.userId,
                        name: userData.userName,
                        email: userData.userEmail,
                        avatar: userData.userPictureUrl,
                        access: userData.userRole
                    };
                }

                return cachedUserData;
            }
        });

        $scope.modalResponse = null;

        /**
         * Close the modal.
         */
        $scope.close = function () {
            Mousetrap.unbind(['left', 'right']);
            const nextUrl = getUrlPathAndSearch(setParameter($location.absUrl(), 'frid', null));
            $location.url(nextUrl);
            $scope.modalResponse = null;
        };

        $scope.isLastResponse = false;
        $scope.isFirstResponse = false;

        /**
         * Select a new response (causes an update to the modal).
         *
         * @param response A complete FeedbackResponse.
         */
        function selectResponse(response) {
            $scope.organizationId = UserData.currentSite?.organization_id;
            $scope.modalResponse = response;

            var responseIndex = _.findIndex($scope.feedbackResponses, { id: response.id });
            $scope.isFirstResponse = responseIndex === -1 || responseIndex === 0;
            $scope.isLastResponse = responseIndex === -1 || responseIndex === $scope.feedbackResponses.length - 1;

            const nextUrl = getUrlPathAndSearch(setParameter($location.absUrl(), 'frid', response.id));
            $location.url(nextUrl);
        }

        /**
         * Select the previous (newer) response in the list.
         */
        $scope.previous = function () {
            var prevResponse =
                $scope.feedbackResponses[_.findIndex($scope.feedbackResponses, $scope.modalResponse) - 1];

            if (prevResponse) {
                selectResponse(prevResponse);
            }
        };

        /**
         * Select the next (older) response in the list.
         */
        $scope.next = function () {
            var nextResponse =
                $scope.feedbackResponses[_.findIndex($scope.feedbackResponses, $scope.modalResponse) + 1];

            if (nextResponse) {
                selectResponse(nextResponse);
            }
        };

        $scope.deleteCurrentResponse = function () {
            $scope.deleteResponse($scope.modalResponse, function () {
                if (!$scope.isLastResponse) {
                    $scope.next();
                } else {
                    $scope.close();
                }
            });
        };

        $scope.onCommentAdded = (comment) => {
            $timeout(() => {
                addComment($scope.modalResponse.id, comment.author.id);
            });
        };

        $scope.onCommentRemoved = (comment, wasLastAuthorComment) => {
            $timeout(() => {
                removeComment($scope.modalResponse.id, comment.author.id, wasLastAuthorComment);
            });
        };
        $scope.getSort = () => {
            const searchParams = new URLSearchParams(window.location.search);
            const sortBySearchParam = searchParams.get('sort_by');
            if (!sortBySearchParam) {
                try {
                    const sortBy = LocalStorage.get('FBResponsesSort');
                    if (sortBy) {
                        searchParams.set('sort_by', sortBy);
                        window.history.replaceState(
                            null,
                            '',
                            `${window.location.pathname}?${searchParams.toString()}`.replaceAll('%3A', ':')
                        );
                        return sortBy;
                    }
                    return '-index';
                } catch {
                    return '-index';
                }
            }
            return sortBySearchParam;
        };
    }
]);
